import { ChangeDetectionStrategy, Component, computed, effect, EventEmitter, input, model, OnInit, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TripDetail } from 'src/app/api';
import { Journals, TripJournalService, VideoTypes } from 'src/app/api/trip-journal.service';
import { ConfigService } from 'src/app/config.service';
import { ErrorService } from 'src/app/services/error.service';
import { IconService } from 'src/app/ui/icon.service';
import { ProseMirrorComponent } from 'src/app/ui/prose-mirror/prose-mirror.component';
import { SaveButtonComponent } from 'src/app/ui/save-button/save-button.component';
import { ImageService } from 'src/app/utility/image.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SanitizeHtmlPipe } from 'src/app/ui/pipes/safe-html.pipe';
import { YoutubeEmbedComponent } from 'src/app/ui/youtube-embed/youtube-embed.component';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { Observable } from 'rxjs';
import { UrlService } from 'src/app/services/url.service';
import { defaultJournalAuthorForUser } from 'src/app/utility/journal-author.util';

@Component({
  selector: 'app-trip-journal-edit',
  imports: [YoutubeEmbedComponent, SaveButtonComponent, ButtonModule, ProseMirrorComponent, ReactiveFormsModule, FormsModule, InputTextModule, FontAwesomeModule, SelectButtonModule],
  templateUrl: './trip-journal-edit.component.html',
  styleUrl: './trip-journal-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TripJournalEditComponent implements OnInit {
  trip = input<TripDetail>()
  editEntry = input<Journals>()


  imageUrl: WritableSignal<string | ArrayBuffer | null> = signal(null);

  @Output() entryEdited: EventEmitter<Journals> = new EventEmitter<Journals>()
  @Output() entryCreated: EventEmitter<Journals> = new EventEmitter<Journals>()
  @Output() cancel: EventEmitter<void> = new EventEmitter<void>()
  @ViewChild(SaveButtonComponent) saveButton!: SaveButtonComponent
  imageFile: File | undefined

  /** True only when the user explicitly removed the image in this edit session. */
  photoRemoved = signal<boolean>(false)

  videoId = signal<string | null>(null)

  isCreateMode = computed(() => !this.editEntry())

  private imageSelectionId = 0

  entryTypes = signal<{ label: string, value: string }[]>([
    { label: "Picture + text", value: "picture" },
    { label: "Video", value: "video" },
  ])

  constructor(
    private tripJournalService: TripJournalService,
    private errorService: ErrorService,
    private messageService: MessageService,
    private config: ConfigService,
    private imageService: ImageService,
    public icons: IconService,
    private fb: FormBuilder,
    private authService: LocalAuthService,
    private urlService: UrlService
  ) {

    effect(() => {
      const entry = this.editEntry()

      // Always reset photo-edit state when the target entry changes (including create mode).
      this.resetImageEditState()

      if (entry) {
        this.form.controls.author.setValue(entry.author ?? "")
        this.form.controls.title.setValue(entry.entrytitle ?? "")
        this.form.controls.entry.setValue(entry.entry ?? "")
        this.form.controls.entryType.setValue(entry.videoId ? "video" : "picture")
        this.form.controls.videoUrl.setValue(entry.videoId ? "https://www.youtube.com/watch?v=" + entry.videoId : "")
        this.videoId.set(entry.videoId ?? null)

        if (entry.photo === 1) {
          this.imageUrl.set(this.config.tripJournaPhotoUrl + "/" + entry.tripId + '/' + entry.id + '.jpg')
        }
        else {
          this.imageUrl.set(null)
        }
      }
      else {
        this.clearFormFields()
      }
    })
  }

  ngOnInit(): void {

    this.form.controls.videoUrl.valueChanges.subscribe((value) => {
      const videoUrl = value

      if (!videoUrl) {
        this.videoId.set(null)
      }
      else {
        this.videoId.set(this.urlService.getYoutubeVideoId(videoUrl))
      }


    })

    this.form.controls.entryType.valueChanges.subscribe((value) => {
      if (value === "video") {
        this.form.controls.videoUrl.enable()

        this.form.controls.entry.disable()

      }
      else if (value === "picture") {
        this.form.controls.videoUrl.disable()

        this.form.controls.entry.enable()

      }
    })

  }

  form = this.fb.group({
    title: this.fb.control<string>("", { validators: [Validators.required] }),
    author: this.fb.control<string>(defaultJournalAuthorForUser(this.authService.currentUser), { validators: [Validators.required] }),
    entry: this.fb.control<string>("", { validators: [Validators.required] }),
    entryType: this.fb.control<string>("picture", { validators: [] }),
    videoUrl: this.fb.control<string>("", { validators: [] }),

  })

  clear() {
    this.clearFormFields()
    this.resetImageEditState()
  }

  private clearFormFields(): void {
    const currentUser = this.authService.currentUser;
    this.form.controls.author.setValue(defaultJournalAuthorForUser(currentUser))
    this.form.controls.title.setValue("")
    this.form.controls.entry.setValue("")
    this.form.controls.entryType.setValue("picture")
    this.form.controls.videoUrl.setValue("")
    this.videoId.set(null)
    this.imageUrl.set(null)
  }

  saveEntry() {
    const entry = this.editEntry()
    this.form.markAllAsTouched()

    if (!this.form.valid) {
      this.saveButton.saveComplete()
      return;
    }

    let observable: Observable<Journals>
    let emitter: EventEmitter<Journals>
    const isVideoEntry = this.form.controls.entryType.value === 'video'
    const shouldRemovePhoto = isVideoEntry || this.photoRemoved()

    if (entry) {
      // Edit existing entry
      if (isVideoEntry) {
        this.videoId.set(this.urlService.getYoutubeVideoId(this.form.controls.videoUrl.value ?? "") )
      }
      else {
        this.form.controls.videoUrl.setValue("")
        this.videoId.set(null)
      }

      observable = this.tripJournalService.editStaffJournalEntry(entry.id, {
        author: this.form.controls.author.value ?? "",
        title: this.form.controls.title.value ?? "",
        content: this.form.controls.entry.value ?? "",
        videoId: this.videoId(),
        videoType: this.videoId() ? VideoTypes.YouTube : null
      })
      emitter = this.entryEdited
    } else {
      // Create new entry
      if (!isVideoEntry) {
        this.videoId.set(null)
      }

      const entryData = {
        author: this.form.controls.author.value ?? "",
        title: this.form.controls.title.value ?? "",
        content: isVideoEntry ? "" : (this.form.controls.entry.value ?? ""),
        videoId: this.videoId(),
        videoType: this.videoId() ? VideoTypes.YouTube : null
      };

     observable = this.tripJournalService.addStaffJournalEntry(entryData, this.trip()?.id ?? 0)
     emitter = this.entryCreated
    }

    observable.subscribe({
      next: (result) => {
        if (this.imageFile) {
          this.tripJournalService.addJournalEntryPhoto(result.tripId, result.id, this.imageFile).subscribe({
            next: () => {
              this.resetImageEditState()
              emitter.emit({ ...result, photo: 1 })
              this.saveButton.saveComplete()
            },
            error: (err) => {
              this.errorService.error("The journal entry was saved, but the photo failed to upload.  Please edit the journal entry and try uploading the photo again", err, this.messageService)
              emitter.emit(result)
              this.saveButton.saveComplete()
            }
          })
        }
        else if (shouldRemovePhoto && entry) {
          // Only remove when editing an existing entry and the user removed the image
          // (or converted the entry to video). Never remove based on leftover create state.
          this.tripJournalService.removeJournalEntryPhoto(result.id).subscribe({
            next: () => {
              this.resetImageEditState()
              emitter.emit({ ...result, photo: 0 })
              this.saveButton.saveComplete()
            },
            error: (err) => {
              this.errorService.error("The journal entry was saved, but the photo failed to remove.  Please edit the journal entry and try removing the photo again", err, this.messageService)
              emitter.emit(result)
              this.saveButton.saveComplete()
            }
          })
        }
        else {
          emitter.emit(result)
          this.saveButton.saveComplete()
        }
      },
      error: (err) => {
        this.errorService.error("There was a problem saving the journal entry", err, this.messageService)
        this.saveButton.saveComplete()
      }
    })
  }

  private resetImageEditState(): void {
    this.imageSelectionId++
    this.imageFile = undefined
    this.photoRemoved.set(false)
  }

  onFileSelected(event: Event) {

    if (event.target) {
      const target = event.target as HTMLInputElement

      if (target?.files && target.files.length > 0) {
        const file = target.files[0];
        const selectionId = ++this.imageSelectionId

        // Resize the image before setting it
        this.imageService.resizeImage(file, 1920, 1080).then((resizedFile) => {
          // Ignore stale async results after clear/reset or a newer selection
          if (selectionId !== this.imageSelectionId) {
            return
          }

          this.imageFile = resizedFile;
          this.photoRemoved.set(false)

          const reader = new FileReader();
          reader.onload = () => {
            if (selectionId !== this.imageSelectionId) {
              return
            }
            this.imageUrl.set(reader.result);
          };
          reader.readAsDataURL(resizedFile);
        });

      }
    }
  }


  removeImage() {
    this.imageSelectionId++
    this.imageFile = undefined
    this.imageUrl.set(null)
    this.photoRemoved.set(true)
  }

}
